import type { TaskPriority } from '@travel-planner/shared/schemas/tasks';
import type { CSSProperties } from 'react';
import { ChevronDown } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutationToasts } from '@/shared/lib';
import { pickCss } from '@/shared/lib/css-utils';
import { useUpdateTask } from '../api/use-update-task';
import css from './task-priority-cell.module.scss';

const dotStyles = pickCss(css, css.dot);
const triggerStyles = pickCss(css, css.trigger);

const OPTIONS: ReadonlyArray<{ value: TaskPriority; label: string }> = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const LABEL_BY: Record<TaskPriority, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

type TaskPriorityCellProps = {
  taskId: string;
  priority: TaskPriority;
};

/**
 * Inline color-coded priority dropdown. Renders the menu via React
 * portal so the picker escapes `overflow: hidden` clipping on the
 * shared `<TableContainer>` and `<TableCell>` (matches the pattern
 * the shared Tooltip uses). Mirrors legacy red / amber / green color
 * coding by priority.
 */
export const TaskPriorityCell = ({
  taskId,
  priority,
}: TaskPriorityCellProps) => {
  const mutation = useUpdateTask();
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState<CSSProperties | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useMutationToasts(mutation, { success: 'Priority updated' });

  useLayoutEffect(() => {
    if (!open) return;
    const node = triggerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    // The menu's natural width is at least the trigger width (~130px).
    // Right-align when the trigger sits close enough to the viewport's
    // right edge that left-anchoring would clip the menu — then clamp
    // to a small margin so we never sit under the scrollbar.
    const viewportPadding = 8;
    const menuWidth = Math.max(rect.width, 140);
    const wouldOverflowRight =
      rect.left + menuWidth + viewportPadding > window.innerWidth;
    const left = wouldOverflowRight
      ? Math.max(viewportPadding, rect.right - menuWidth)
      : rect.left;
    setOffset({
      left,
      top: rect.bottom + 4,
      // Slightly wider than the trigger so option labels + their dots
      // have breathing room on both sides instead of hugging the edge.
      minWidth: Math.max(rect.width + 16, 150),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      // Close when clicking anywhere outside the trigger or menu —
      // the menu lives in a portal so we can't rely on `contains`
      // through the React tree. Check the trigger explicitly and
      // exclude clicks marked as inside the menu via a data-attribute.
      if (triggerRef.current?.contains(e.target as Node)) return;
      const target = e.target as HTMLElement;
      if (target.closest('[data-priority-menu]')) return;
      setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const handleSelect = (next: TaskPriority) => {
    setOpen(false);
    if (next !== priority) {
      mutation.mutate({ id: taskId, priority: next });
    }
  };

  return (
    <span className={css.root}>
      <span aria-hidden className={dotStyles({ priority })} />
      <button
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Priority: ${LABEL_BY[priority]}`}
        className={triggerStyles({ priority, isOpen: open })}
        disabled={mutation.isPending}
        onClick={() => setOpen((p) => !p)}
        ref={triggerRef}
        type="button"
      >
        <span className={css.triggerLabel}>{LABEL_BY[priority]}</span>
        <ChevronDown className={css.chevron} />
      </button>
      {open && offset
        ? createPortal(
            <div className={css.menu} data-priority-menu style={offset}>
              {OPTIONS.map((opt) => (
                <button
                  className={
                    opt.value === priority ? css.optionIsSelected : css.option
                  }
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  type="button"
                >
                  <span
                    aria-hidden
                    className={dotStyles({ priority: opt.value })}
                  />
                  <span className={css.optionLabel}>{opt.label}</span>
                </button>
              ))}
            </div>,
            document.body
          )
        : null}
    </span>
  );
};
